import { Input, Stack, Text, Textarea } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import * as React from "react";
import { Control, Controller, useForm } from "react-hook-form";
import { z } from "zod";

import {
  createContentLibraryItem,
  updateContentLibraryItem,
} from "../api/company-knowledge";
import { useLoading, useToaster } from "../hooks";
import {
  BusinessFeature,
  ContentLibraryItem,
  CustomerProfileSubset,
  MessagingPillar,
} from "../types/company-knowledge";
import { Button } from "./button";
import { Checkbox } from "./checkbox";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "./dialog";
import { Field } from "./field";
import { toaster } from "./toaster";

// An item passes `item` to edit an existing one, or null to create.
// The subsets, features, and pillars come from the parent page, which
// loads them alongside the items for the checkbox groups.

const contentLibrarySchema = z.object({
  title: z.string().trim().min(1, "A title is required."),
  context: z.string(),
  "content-url": z
    .string()
    .refine(
      (value) => value === "" || /^https?:\/\//.test(value),
      "Enter a valid URL (starting with http:// or https://), or leave it blank.",
    ),
  "subset-ids": z.array(z.number()),
  "related-feature-ids": z.array(z.number()),
  "related-pillar-ids": z.array(z.number()),
});

type ContentLibraryFormData = z.infer<typeof contentLibrarySchema>;

interface ContentLibraryDialogProps {
  item?: ContentLibraryItem | null;
  isOpen: boolean;
  onOpenChange: (details: { open: boolean }) => void;
  onSuccess: () => void;
  subsets: CustomerProfileSubset[];
  features: BusinessFeature[];
  pillars: MessagingPillar[];
  workspaceSlug: string;
}

export function ContentLibraryDialog({
  item,
  isOpen,
  onOpenChange,
  onSuccess,
  subsets,
  features,
  pillars,
  workspaceSlug,
}: ContentLibraryDialogProps) {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const [isLoading, withLoading] = useLoading();
  const withToaster = useToaster();
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<ContentLibraryFormData>({
    resolver: zodResolver(contentLibrarySchema),
    defaultValues: emptyItem,
  });

  React.useEffect(() => {
    if (!isOpen) return;
    reset(item ? toFormData(item) : emptyItem);
  }, [isOpen, item, reset]);

  const onSubmit = async (data: ContentLibraryFormData) => {
    await withLoading(async () => {
      await withToaster(async () => {
        if (item) {
          await updateContentLibraryItem(workspaceSlug, item.id, data);
          toaster.create({ type: "success", title: "Content item updated" });
        } else {
          await createContentLibraryItem(workspaceSlug, data);
          toaster.create({ type: "success", title: "Content item created" });
        }
        onOpenChange({ open: false });
        onSuccess();
      });
    });
  };

  return (
    <DialogRoot open={isOpen} onOpenChange={onOpenChange} size="md">
      <DialogContent ref={ref}>
        <DialogCloseTrigger />
        <DialogHeader>
          <DialogTitle>
            {item ? "Edit Content Item" : "Create Content Item"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogBody paddingBottom="6">
            <Stack gap="4">
              <Field
                label="Title"
                invalid={!!errors.title}
                errorText={errors.title?.message}
              >
                <Input
                  size="sm"
                  placeholder="The content's title"
                  {...register("title")}
                />
              </Field>
              <Field
                label="Description and useful takeaways"
                helperText="Include what the agent should know from this material. Saving a link or file does not read its contents."
                invalid={!!errors.context}
                errorText={errors.context?.message}
              >
                <Textarea
                  {...register("context")}
                  placeholder="What this material is and what should be taken from it."
                  rows={3}
                />
              </Field>
              <Field
                label="Content URL"
                invalid={!!errors["content-url"]}
                errorText={errors["content-url"]?.message}
              >
                <Input
                  {...register("content-url")}
                  placeholder="https://example.com/article"
                />
              </Field>
              <KnowledgeCheckboxGroup
                control={control}
                helperText="Applies to all subsets when none are selected."
                items={subsets}
                label="Applicable ICP subsets"
                name="subset-ids"
              />
              <KnowledgeCheckboxGroup
                control={control}
                helperText="Optional."
                items={features}
                label="Related business features"
                name="related-feature-ids"
              />
              <KnowledgeCheckboxGroup
                control={control}
                helperText="Optional."
                items={pillars}
                label="Related messaging pillars"
                name="related-pillar-ids"
              />
            </Stack>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => onOpenChange({ open: false })}
            >
              Cancel
            </Button>
            <Button loading={isLoading} type="submit">
              {item ? "Save Changes" : "Create Content Item"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </DialogRoot>
  );
}

// File-local helper for the three id-array checkbox groups. Field is
// deliberately avoided: a Chakra Field shares its label/input ids
// with every control inside it, which makes each checkbox's label
// point at the first checkbox's input.
interface KnowledgeCheckboxGroupProps {
  control: Control<ContentLibraryFormData>;
  helperText: string;
  items: { id: number; name: string }[];
  label: string;
  name: "subset-ids" | "related-feature-ids" | "related-pillar-ids";
}

function KnowledgeCheckboxGroup({
  control,
  helperText,
  items,
  label,
  name,
}: KnowledgeCheckboxGroupProps) {
  return (
    <Stack gap="1">
      <Text fontSize="sm" fontWeight="medium" color="fg.muted">
        {label}
      </Text>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <Stack gap="2">
            {items.map((item) => {
              const checked = field.value.includes(item.id);
              return (
                <Checkbox
                  key={item.id}
                  checked={checked}
                  onCheckedChange={(details) => {
                    const next = details.checked
                      ? [...field.value, item.id]
                      : field.value.filter((id) => id !== item.id);
                    field.onChange(next);
                  }}
                >
                  {item.name}
                </Checkbox>
              );
            })}
            <Text color="fg.muted" fontSize="sm" fontStyle="italic">
              {helperText}
            </Text>
          </Stack>
        )}
      />
    </Stack>
  );
}

const emptyItem: ContentLibraryFormData = {
  title: "",
  context: "",
  "content-url": "",
  "subset-ids": [],
  "related-feature-ids": [],
  "related-pillar-ids": [],
};

// The schema's nullable fields arrive as null over the wire; empty
// strings are the form's representation of "unset".
const toFormData = (item: ContentLibraryItem): ContentLibraryFormData => ({
  title: item.title ?? "",
  context: item.context ?? "",
  "content-url": item["content-url"] ?? "",
  "subset-ids": item["subset-ids"] ?? [],
  "related-feature-ids": item["related-feature-ids"] ?? [],
  "related-pillar-ids": item["related-pillar-ids"] ?? [],
});
